import { useEffect, useMemo, useState } from "react";
import {
  Plus, Loader2, Pencil, ReceiptText, Trash2, FileUp, Upload, Maximize2, Minimize2, Undo2, FileSpreadsheet, MapPin, Hash,
  AlertTriangle, CheckCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import LineGrid from "./LineGrid";
import SummaryPanel from "./SummaryPanel";
import SanityFlagsPanel from "./SanityFlagsPanel";
import LibraryPromptDialog from "./LibraryPromptDialog";
import QualificationsSection from "./QualificationsSection";
import IncompleteLinesDialog from "./IncompleteLinesDialog";
import AddLinesDialog from "./AddLinesDialog";
import LineBulkEditDialog from "./LineBulkEditDialog";
import VendorQuoteDialog from "./VendorQuoteDialog";
import ImportHistoryButton from "./ImportHistoryButton";
import ConfirmDialog from "./ConfirmDialog";
import FrsUploadDialog from "./FrsUploadDialog";
import ItemDrawer from "./ItemDrawer";
import { estApi } from "../lib/api";

export default function EstimateTab({ estimate, onTotals, reloadKey = 0, readOnly = false, expanded = false, onToggleExpand }) {
  const id = estimate.id;
  const [data, setData] = useState(null);
  const [options, setOptions] = useState({ cost_codes: [], cost_types: [] });
  const [settings, setSettings] = useState({});
  const [selected, setSelected] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteTargets, setQuoteTargets] = useState([]);
  const [frsOpen, setFrsOpen] = useState(false);
  const [batches, setBatches] = useState([]);
  const [showLocations, setShowLocations] = useState(true);
  const [showItemNumbers, setShowItemNumbers] = useState(true);
  const [priceThemOpen, setPriceThemOpen] = useState(false);
  const [newItemSection, setNewItemSection] = useState(null);

  const loadBatches = () => estApi.getTakeoffBatches(id).then(setBatches).catch(() => {});

  const apply = (res) => {
    setData(res);
    if (res?.rollup) onTotals(res.rollup);
  };

  useEffect(() => {
    estApi.getLines(id).then(apply).catch(() => toast.error("Could not load estimate lines"));
    estApi.getCodeOptions().then(setOptions).catch(() => {});
    estApi.getSettings().then(setSettings).catch(() => {});
    estApi.getPrefs().then((p) => {
      setShowLocations(p.show_locations !== false);
      setShowItemNumbers(p.show_item_numbers !== false);
    }).catch(() => {});
    loadBatches();
    // eslint-disable-next-line
  }, [id, reloadKey]);

  const lines = data?.rollup?.lines || [];
  const sections = useMemo(
    () => [...new Set(lines.map((l) => l.section).filter(Boolean))], [lines]);

  const guard = (promise) => promise.then(apply).catch((e) => {
    toast.error(e?.response?.data?.detail || "Something went wrong");
  });

  const [libPrompt, setLibPrompt] = useState(null);
  const onEdit = async (line, patch) => {
    const res = await guard(estApi.updateLine(line.id, patch));
    // EST-FRS-2 §4: a hand-typed unit price on a part the library has no cost for → "Add to cost library?"
    if (patch.unit_cost > 0 && !line.vendor_quote_id && !line.library_prompt_answered && line.library_status !== "ok") {
      setLibPrompt({ ...line, unit_cost: patch.unit_cost, library_status: line.library_status });
    }
    return res;
  };
  const proposeLibrary = (line) => {
    const note = window.prompt(`Propose $${line.unit_cost} as the new library price for ${line.item_number || line.description}? Add a note:`);
    if (note === null) return;
    estApi.proposeLibraryUpdate({ item_id: line.price_list_item_id, proposed_unit_cost: line.unit_cost, line_id: line.id, note })
      .then(() => toast.success("Proposed — an admin approves it in Settings › Estimating")).catch((e) => toast.error(e?.response?.data?.detail || "Not proposed"));
  };

  const toggleItemNumbers = () => {
    const next = !showItemNumbers;
    setShowItemNumbers(next);
    estApi.savePrefs({ show_item_numbers: next }).catch(() => {});
  };

  const toggleLocations = () => {
    const next = !showLocations;
    setShowLocations(next);
    estApi.savePrefs({ show_locations: next }).catch(() => {});   // sticks for this user
  };

  const [confirmZeroOpen, setConfirmZeroOpen] = useState(false);
  const incomplete = lines.filter((l) => l.incomplete);
  const confirmAllZero = () => guard(estApi.bulkLines({
    estimate_id: id, line_ids: incomplete.map((l) => l.id), cost_confirmed_zero: true,
  })).then(() => {
    setConfirmZeroOpen(false);
    toast.success(`${incomplete.length} line(s) confirmed at $0`);
  });

  const onMove = (line, dir) => {
    const ids = lines.map((l) => l.id);
    const i = ids.indexOf(line.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    return guard(estApi.reorderLines(id, ids));
  };

  const openQuote = (line) => {
    setQuoteTargets(line ? [line] : lines.filter((l) => selected.includes(l.id)));
    setQuoteOpen(true);
  };

  const deleteSelected = () =>
    guard(estApi.bulkLines({ estimate_id: id, line_ids: selected, delete: true }))
      .then(() => { setSelected([]); toast.success("Lines deleted"); });

  if (!data) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading estimate…
      </div>
    );
  }

  return (
    <div className="flex items-start gap-4" data-testid="estimate-tab">
      <div className="min-w-0 flex-1 space-y-3">
        {readOnly && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
               data-testid="pricing-readonly-banner">
            This change order is approved, so its pricing is locked. Corrections go on a new change
            order.
          </div>
        )}
        <div className={`flex items-center gap-2 ${readOnly ? "hidden" : ""}`}>
          <Button size="sm" className="text-xs" onClick={() => setAddOpen(true)}
                  data-testid="add-lines-btn">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Scope
          </Button>
          <Button variant="data" size="sm" className="text-xs" onClick={() => setFrsOpen(true)}
                  title="Upload an FRS workbook or a mapped takeoff spreadsheet"
                  data-testid="upload-takeoff-btn">
            <Upload className="mr-1.5 h-3.5 w-3.5" /> Takeoff
          </Button>
          <Button variant="data" size="sm" className="text-xs" onClick={() => openQuote(null)}
                  data-testid="vendor-quote-btn">
            <Upload className="mr-1.5 h-3.5 w-3.5" /> Quote
          </Button>
          <Button variant={showItemNumbers ? "secondary" : "outline"} size="icon"
                  className="h-8 w-8" onClick={toggleItemNumbers}
                  title={showItemNumbers ? "Hide the manufacturer and part number under each item"
                                         : "Show the manufacturer and part number under each item"}
                  data-testid="toggle-item-numbers-btn">
            <Hash className="h-3.5 w-3.5" />
          </Button>
          <Button variant={showLocations ? "secondary" : "outline"} size="icon"
                  className="h-8 w-8" onClick={toggleLocations}
                  title={showLocations ? "Hide locations under each item" : "Show locations under each item"}
                  data-testid="toggle-locations-btn">
            <MapPin className="h-3.5 w-3.5" />
          </Button>
          <ImportHistoryButton
            batches={batches}
            onUndo={(batchId) => { toast.dismiss(); return guard(estApi.undoTakeoffBatch(id, batchId))
              .then(() => { loadBatches(); toast.success("Import undone"); }); }}
          />
          {selected.length > 0 && (
            <>
              <span className="text-xs text-slate-500" data-testid="lines-selected-count">
                {selected.length} selected
              </span>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setBulkOpen(true)}
                      data-testid="bulk-edit-btn">
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Bulk edit
              </Button>
              <Button variant="ghost" size="sm" className="text-xs text-red-600"
                      onClick={deleteSelected} data-testid="bulk-delete-btn">
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
              </Button>
            </>
          )}
          {onToggleExpand && (
            <Button variant="nav" size="sm" className="ml-auto h-8 w-8 p-0" onClick={onToggleExpand} title={expanded ? "Exit full screen" : "Full screen"}
                    aria-label={expanded ? "Exit full screen" : "Full screen"} data-testid="estimate-expand-btn">
              {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            </Button>)}
        </div>

        {incomplete.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
               data-testid="incomplete-banner">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span><strong>{incomplete.length} line(s) incomplete</strong></span>
            {!readOnly && (
              <>
                <Button variant="outline" size="sm" className="ml-auto h-6 text-[11px]"
                        onClick={() => setPriceThemOpen(true)} data-testid="price-incomplete-btn">
                  <Pencil className="mr-1 h-3 w-3" /> Price them
                </Button>
                <Button variant="outline" size="sm" className="h-6 text-[11px]"
                        onClick={() => setConfirmZeroOpen(true)} data-testid="confirm-all-zero-btn">
                  <CheckCheck className="mr-1 h-3 w-3" /> Confirm all as $0
                </Button>
              </>
            )}
          </div>
        )}

        {data?.sanity && <SanityFlagsPanel estimateId={id} sanity={data.sanity} readOnly={readOnly} onChanged={(s) => setData((d) => ({ ...d, sanity: s }))} />}
        {libPrompt && <LibraryPromptDialog line={libPrompt} onClose={() => { setLibPrompt(null); estApi.getLines(id).then(apply); }} onDone={() => { setLibPrompt(null); estApi.getLines(id).then(apply); }} />}
        <LineGrid
          readOnly={readOnly}
          onProposeLibrary={proposeLibrary}
          showLocations={showLocations}
          showItemNumbers={showItemNumbers}
          lines={lines}
          selected={selected}
          toggleSelect={(lineId) => setSelected((s) =>
            s.includes(lineId) ? s.filter((x) => x !== lineId) : [...s, lineId])}
          toggleSelectAll={() => setSelected((s) => {
            const real = lines.filter((l) => !l.internal_only).map((l) => l.id);
            return s.length === real.length ? [] : real;
          })}
          onEdit={onEdit}
          onDuplicate={(l) => guard(estApi.duplicateLine(l.id))}
          onDelete={(l) => guard(estApi.deleteLine(l.id))}
          onMove={onMove}
          onQuote={openQuote}
          quotes={data?.quotes || []}
          onQuoteMode={(q, mode) => guard(estApi.setQuotePriceMode(id, q.id, mode))}
          onQuoteRemove={(q) => guard(estApi.deleteQuote(id, q.id))}
        />

        <QualificationsSection estimateId={id} readOnly={readOnly} sections={sections} />
      </div>

      <SummaryPanel
        readOnly={readOnly}
        defaults={data.defaults}
        rollup={data.rollup}
        misc={data.misc_lines}
        costCodes={options.cost_codes}
        settingsTexturaRate={settings.textura_rate}
        onSaveDefaults={(patch) => guard(estApi.saveDefaults(id, patch))}
        onApplyGm={() => guard(estApi.applyGm(id, {}))
          .then(() => toast.success("Line overrides cleared — defaults applied"))}
        onAddMisc={(row) => guard(estApi.addMisc(id, row))}
        onUpdateMisc={(miscId, row) => guard(estApi.updateMisc(id, miscId, row))}
        onDeleteMisc={(miscId) => guard(estApi.deleteMisc(id, miscId))}
      />

      <ItemDrawer
        open={newItemSection !== null} onOpenChange={(o) => !o && setNewItemSection(null)}
        item={{}} options={options}
        onSaved={(saved) => {
          // A part created here goes straight onto the scope it was created from.
          const section = newItemSection || "";
          setNewItemSection(null);
          guard(estApi.addLines(id, { section, price_list_item_ids: [saved.id], qty: 1 }))
            .then(() => toast.success(`${saved.name} added to the cost library and this estimate`));
        }}
      />

      <AddLinesDialog
        open={addOpen} onOpenChange={setAddOpen} sections={sections}
        onNewItem={(section) => { setAddOpen(false); setNewItemSection(section || ""); }}
        onAdd={(body) => estApi.addLines(id, body).then((res) => {
          apply(res);
          toast.success(`${res.added} line(s) added`);
        })}
      />
      <LineBulkEditDialog
        open={bulkOpen} onOpenChange={setBulkOpen} count={selected.length}
        costCodes={options.cost_codes}
        onApply={(payload) =>
          guard(estApi.bulkLines({ estimate_id: id, line_ids: selected, ...payload }))
            .then(() => toast.success("Lines updated"))}
      />
      <VendorQuoteDialog
        open={quoteOpen} onOpenChange={setQuoteOpen} estimateId={id}
        targetLines={quoteTargets} costCodes={options.cost_codes} costTypes={options.cost_types}
        onApplied={apply}
      />
      <ConfirmDialog
        open={confirmZeroOpen} onOpenChange={setConfirmZeroOpen}
        title="Are you sure all costs are $0?"
        body={`This marks all ${incomplete.length} incomplete line(s) as deliberately $0 and unblocks sending the proposal. They will carry no cost and no margin.`}
        confirmLabel={`Yes, confirm all ${incomplete.length} as $0`}
        onConfirm={confirmAllZero}
        testid="confirm-all-zero-dialog"
      />

      <IncompleteLinesDialog
        open={priceThemOpen} onOpenChange={setPriceThemOpen} estimateId={id}
        detail={{ lines: incomplete,
                  message: `${incomplete.length} line(s) still need a cost or a margin.` }}
        onResolved={() => estApi.getLines(id).then(apply).catch(() => {})}
      />

      <FrsUploadDialog
        open={frsOpen} onOpenChange={setFrsOpen} estimateId={id}
        onCommitted={(res) => { apply(res); loadBatches(); }}
      />
    </div>
  );
}
